/**
 * 无限画布 · UI 设计节点：DeepSeek 策划多屏界面提示词 → 拆分到图片节点出图
 */

import {
  normalizeEcomPromptPlanRow,
  resolveEcomSeedreamOutputSize
} from '@/utils/ecomSeedreamSizes'

export const UI_DESIGN_PLATFORMS = [
  { id: 'app', label: 'App / 原生' },
  { id: 'mini', label: '小程序' },
  { id: 'web', label: 'Web 桌面' },
  { id: 'responsive', label: '响应式（移动优先）' }
]

/** 自定义风格：使用 designStyleCustom 文本 */
export const UI_DESIGN_STYLE_CUSTOM_ID = 'custom'

/** 内置 UI 设计风格模板（20 种 + 自定义） */
export const UI_DESIGN_BUILTIN_STYLES = [
  {
    id: 'flatMinimal',
    label: '极简扁平风',
    desc: '纯色块、无立体、清晰层级',
    prompt:
      '极简扁平风 UI：大面积纯色底；无渐变与立体阴影；图标与按钮扁平化；主色 1～2 种+中性灰阶；圆角 8～12px；无衬线字体（微软雅黑/Inter 气质）；信息层级靠字号与留白区分；禁止 skeuomorphism。'
  },
  {
    id: 'softNeumorphism',
    label: '轻拟物质感风',
    desc: '柔和凸起/凹陷、低对比立体',
    prompt:
      '轻拟物质感风 UI：同色系浅底（#E8ECF0～#F0F2F5）；元素轻微外凸/内凹（双向软阴影，非强烈新拟态）；圆角 14～18px；主色柔和蓝或灰蓝；按钮与卡片触感柔软；图标简约填充。'
  },
  {
    id: 'neumorphism',
    label: '新拟态风',
    desc: 'Neumorphism 双阴影、同色系浮雕',
    prompt:
      '新拟态风 Neumorphism UI：背景与控件同色系（浅灰 #E0E5EC）；明显双向阴影（亮边+暗边）营造浮雕；圆角 16～24px；无硬边框；开关/卡片/按钮一体浮雕感；主色低饱和；避免高对比描边。'
  },
  {
    id: 'glassmorphism',
    label: '玻璃拟态风',
    desc: '半透明玻璃卡片、描边高光',
    prompt:
      '玻璃拟态 Glassmorphism UI：渐变或摄影背景；半透明磨砂卡片（blur 背景）；1px 白色/浅色描边高光；圆角 16～20px；层次靠透明度与模糊；主按钮实心高对比；图标线性或轻填充。'
  },
  {
    id: 'frostedBlur',
    label: '毛玻璃模糊风',
    desc: '强背景模糊、雾面层次',
    prompt:
      '毛玻璃模糊风 UI：背景图或柔和渐变；前景面板强 backdrop-filter 模糊（雾面）；半透明层叠；大圆角；文字高对比保证可读；导航/浮层均为磨砂玻璃；主色点缀用于 CTA。'
  },
  {
    id: 'ui3dOps',
    label: '3D 运营 UI 风',
    desc: '立体图标、运营 Banner、促销氛围',
    prompt:
      '3D 运营 UI 风：首页含立体 3D 图标/礼物盒/金币等元素；运营 Banner 醒目；按钮微 3D 或渐变；色彩饱和偏营销；卡片承载活动入口；适合拉新促活；保持界面信息仍可读，微软雅黑中文。'
  },
  {
    id: 'illustration25d',
    label: '2.5D 插画风',
    desc: '等距/轻透视插画、场景化',
    prompt:
      '2.5D 插画风 UI：等距或轻透视插画场景（人物/建筑/物品）；界面 UI 框与插画融合；色彩明快有层次；图标可 2.5D 化；留白充足；适合工具、教育、生活类；避免纯照片写实。'
  },
  {
    id: 'kawaiiCartoon',
    label: '卡通萌系风',
    desc: '圆润可爱、高饱和、亲和',
    prompt:
      '卡通萌系风 UI：大圆角 20px+；糖果色或粉彩配色；圆润描边图标与吉祥物感插画；按钮圆 pill 形；字体圆润；表情与装饰元素可爱；适合年轻女性/亲子/宠物类；温暖友好。'
  },
  {
    id: 'dopamine',
    label: '多巴胺活泼风',
    desc: '高饱和撞色、活力、年轻',
    prompt:
      '多巴胺活泼风 UI：高饱和多色撞色（粉/黄/蓝/绿）；大圆角卡片；粗体标题；贴纸/标签元素；动感斜切色块或波浪装饰；按钮鲜艳实心；适合社交、活动、潮流消费；保持可读对比。'
  },
  {
    id: 'memphis',
    label: '孟菲斯潮流风',
    desc: '几何图形、波点、80s 潮流',
    prompt:
      '孟菲斯 Memphis UI：几何色块（圆/三角/波浪线）；黑白描边+高饱和粉/黄/蓝；波点与条纹装饰；非对称排版；海报感模块；适合潮流品牌/创意活动；界面功能区仍清晰。'
  },
  {
    id: 'acidDesign',
    label: '酸性设计风',
    desc: '金属渐变、扭曲、Y2K 酸性',
    prompt:
      '酸性设计 Acid UI：液态金属渐变、镭射/全息质感；扭曲字体或描边字；黑底或深底+荧光色；网格/星形/链条装饰；前卫潮流；适合音乐、夜店、潮牌；关键信息区保证对比度。'
  },
  {
    id: 'cyberTech',
    label: '赛博机能风',
    desc: '赛博朋克、HUD、霓虹线条',
    prompt:
      '赛博机能风 UI：深色底 #0A0E17；霓虹青/品红/黄线条与发光边框；HUD 角标与数据读数；等宽或科技感字体；切角面板；细线图标；适合游戏、硬件、科幻工具；高对比正文。'
  },
  {
    id: 'darkDeep',
    label: '暗黑深邃风',
    desc: '深色沉浸、低噪、高级感',
    prompt:
      '暗黑深邃风 UI：背景 #121212～#1A1A1E；卡片略亮一级 #1E1E24；主色单一强调（蓝/金/紫）；弱发光可选；文字 #E5E7EB；减少装饰；适合阅读、影音、专业工具；OLED 友好。'
  },
  {
    id: 'guochao',
    label: '国潮国风',
    desc: '传统纹样、朱金墨色、现代排版',
    prompt:
      '国潮国风 UI：朱红 #C41E3A、金色 #D4AF37、墨色 #1C1C1C 与现代白底结合；祥云/回纹/山水线稿装饰；书法或宋体标题气质；圆角卡片；适合文创、茶饮、节庆；避免过度堆砌保持可用性。'
  },
  {
    id: 'retroNostalgia',
    label: '复古怀旧风',
    desc: '复古配色、做旧、印刷感',
    prompt:
      '复古怀旧风 UI：米色/牛皮纸底 #F5F0E6；复古绿/棕/橙配色；轻微颗粒纹理；衬线或复古无衬线；邮票/胶片边框元素；适合咖啡、唱片、文艺社区；按钮类似老式标签。'
  },
  {
    id: 'luxuryPremium',
    label: '轻奢高级风',
    desc: '留白、金黑银、精致细线',
    prompt:
      '轻奢高级风 UI：大面积留白；黑/白/香槟金 #C9A962；细线分割；小字号大写英文点缀；卡片轻阴影；无廉价高饱和；适合珠宝、酒店、高端会员；主按钮克制实心或描边金。'
  },
  {
    id: 'wabiSabi',
    label: '日式侘寂风',
    desc: '自然肌理、灰褐、克制留白',
    prompt:
      '日式侘寂风 UI：灰褐/米白/枯木色 #E8E4DF；自然肌理背景；不对称留白；细线图标；低饱和；手工纸质感；适合茶、禅意生活、家居；字体纤细安静。'
  },
  {
    id: 'koreanFresh',
    label: '韩式清新风',
    desc: '浅粉蓝、清爽、ins 感',
    prompt:
      '韩式清新风 UI：浅粉 #FFE4EC、浅蓝 #E0F2FE、白底；大圆角 16px；清爽细线图标；柔和阴影；适合美妆、穿搭、日记类；照片圆角与贴纸感；文字 #374151。'
  },
  {
    id: 'matteFrost',
    label: '磨砂哑光风',
    desc: '哑光表面、无高光塑料感',
    prompt:
      '磨砂哑光风 UI：单色哑光底（灰/莫兰迪色）；控件无玻璃高光；表面细腻磨砂质感；圆角 12px；低对比阴影；适合相机、音频、工具类；图标简约单色填充。'
  },
  {
    id: 'lineSketch',
    label: '线条极简手绘风',
    desc: '线稿图标、手绘边框、极简',
    prompt:
      '线条极简手绘风 UI：黑白或单色线稿图标；1～2px 手绘感描边；留白极大；装饰为简单线条插画；按钮线框为主；适合文艺、独立工具、笔记；避免复杂填色。'
  },
  {
    id: UI_DESIGN_STYLE_CUSTOM_ID,
    label: '自定义',
    desc: '在下方填写你的风格描述',
    prompt: ''
  }
]

/** 旧版 designStyleId → 新版（加载画布时迁移） */
export const UI_DESIGN_LEGACY_STYLE_ID_MAP = {
  petWarm: 'kawaiiCartoon',
  minimal: 'flatMinimal',
  glass: 'frostedBlur',
  material: 'flatMinimal',
  ios: 'koreanFresh',
  corporate: 'flatMinimal',
  ecommerce: 'dopamine',
  dark: 'darkDeep',
  gradient: 'dopamine'
}

export function normalizeUiDesignStyleId(styleId) {
  const id = String(styleId || '').trim()
  if (!id) return UI_DESIGN_DEFAULT_STYLE_ID
  if (id === UI_DESIGN_STYLE_CUSTOM_ID) return id
  if (UI_DESIGN_BUILTIN_STYLES.some(s => s && s.id === id)) return id
  return UI_DESIGN_LEGACY_STYLE_ID_MAP[id] || UI_DESIGN_DEFAULT_STYLE_ID
}

export const UI_DESIGN_DEFAULT_STYLE_ID = 'flatMinimal'

export function findUiDesignBuiltinStyle(styleId) {
  const id = String(styleId || '').trim()
  if (!id) return null
  return UI_DESIGN_BUILTIN_STYLES.find(s => s && String(s.id) === id) || null
}

/** 上游风格解析 JSON 中 builtinStyleHint 可选 id */
export function uiDesignBuiltinStyleHintIds() {
  return UI_DESIGN_BUILTIN_STYLES.filter(s => s.id !== UI_DESIGN_STYLE_CUSTOM_ID)
    .map(s => s.id)
    .join('|')
}

/**
 * 合并内置风格与自定义补充，供 DeepSeek / 兜底提示词使用
 * @param {object} data — 节点 data（designStyleId、designStyleCustom；兼容旧字段 designStyle）
 */
export function resolveUiDesignStyleText(data) {
  const d = data && typeof data === 'object' ? data : {}
  let id = normalizeUiDesignStyleId(d.designStyleId)
  if (!String(d.designStyleId || '').trim()) {
    const legacy = String(d.designStyle || '').trim()
    if (legacy) {
      return legacy
    }
  }
  const hit = findUiDesignBuiltinStyle(id)
  const custom = String(d.designStyleCustom != null ? d.designStyleCustom : '').trim()
  if (id === UI_DESIGN_STYLE_CUSTOM_ID) {
    return custom || String(d.designStyle || '').trim()
  }
  const base = hit && hit.prompt ? String(hit.prompt).trim() : hit ? String(hit.label || '').trim() : ''
  if (custom) {
    return base ? `${base}\n【风格补充】${custom}` : custom
  }
  return base
}

export function uiDesignStyleLabel(data) {
  const d = data && typeof data === 'object' ? data : {}
  const id = normalizeUiDesignStyleId(d.designStyleId)
  const hit = findUiDesignBuiltinStyle(id)
  if (id === UI_DESIGN_STYLE_CUSTOM_ID) {
    const t = String(d.designStyleCustom || d.designStyle || '').trim()
    return t ? `自定义：${t.slice(0, 24)}${t.length > 24 ? '…' : ''}` : '自定义'
  }
  return (hit && hit.label) || id || '极简扁平风'
}

export function uiDesignStyleSummary(data) {
  const d = data && typeof data === 'object' ? data : {}
  const id = normalizeUiDesignStyleId(d.designStyleId)
  const hit = findUiDesignBuiltinStyle(id)
  if (id === UI_DESIGN_STYLE_CUSTOM_ID) {
    return String(d.designStyleCustom || d.designStyle || '').trim() || hit?.desc || ''
  }
  const parts = []
  if (hit && hit.desc) parts.push(hit.desc)
  const custom = String(d.designStyleCustom || '').trim()
  if (custom) parts.push(`补充：${custom}`)
  return parts.join(' · ')
}

/** 策划稿标注尺寸（出图时映射到 Seedream 最小像素） */
export const UI_SIZE_MAIN = '1024x1024'
export const UI_SIZE_DETAIL = '1024x1536'
/** @deprecated 使用 UI_SIZE_DETAIL */
export const UI_SIZE_MOBILE = UI_SIZE_DETAIL
export const UI_SIZE_DESKTOP = '1920x1080'

export const UI_DESIGN_DEFAULT_PRIMARY = '#3B82F6'
export const UI_DESIGN_DEFAULT_SECONDARY = '#64748B'

export const UI_DESIGN_DISPLAY_MODES = [
  { id: 'frameless', label: '无边框纯界面' },
  { id: 'iphone', label: 'iPhone 15 Pro 模型' },
  { id: 'desktop', label: '电脑端模型' }
]

/** 与内置「UI设计专家」Skill 对齐的默认 8 张计划（可经 screenCount 扩展或缩减） */
export const UI_DESIGN_DEFAULT_PLAN = [
  { index: 1, label: '主图1·首页', size: UI_SIZE_MAIN, hint: '首页/核心入口' },
  { index: 2, label: '主图2·核心业务', size: UI_SIZE_MAIN, hint: '核心业务页' },
  { index: 3, label: '主图3·个人中心', size: UI_SIZE_MAIN, hint: '个人中心/重要页' },
  { index: 4, label: '详情图1', size: UI_SIZE_DETAIL, hint: '扩展功能页1' },
  { index: 5, label: '详情图2', size: UI_SIZE_DETAIL, hint: '扩展功能页2' },
  { index: 6, label: '详情图3', size: UI_SIZE_DETAIL, hint: '扩展功能页3' },
  { index: 7, label: '详情图4', size: UI_SIZE_DETAIL, hint: '扩展功能页4' },
  { index: 8, label: '详情图5', size: UI_SIZE_DETAIL, hint: '扩展功能页5' }
]

export const UI_DESIGN_DEFAULT_SCREEN_COUNT = 8
export const UI_DESIGN_SCREEN_COUNT_MIN = 8
export const UI_DESIGN_SCREEN_COUNT_MAX = 16
export const UI_DESIGN_SCREEN_COUNT_CHOICES = Array.from(
  { length: UI_DESIGN_SCREEN_COUNT_MAX - UI_DESIGN_SCREEN_COUNT_MIN + 1 },
  (_, i) => UI_DESIGN_SCREEN_COUNT_MIN + i
)

export function normalizeUiDesignScreenCount(v) {
  const n = Math.round(Number(v))
  if (Number.isNaN(n)) return UI_DESIGN_DEFAULT_SCREEN_COUNT
  return Math.min(
    UI_DESIGN_SCREEN_COUNT_MAX,
    Math.max(UI_DESIGN_SCREEN_COUNT_MIN, n)
  )
}

/** 按界面数量生成策划模板（前 3 张主图，其余详情图） */
export function buildUiDesignPlanTemplate(count) {
  const total = normalizeUiDesignScreenCount(count)
  const out = []
  for (let i = 1; i <= total; i++) {
    const hit = UI_DESIGN_DEFAULT_PLAN.find(p => p.index === i)
    if (hit) {
      out.push({ ...hit })
    } else {
      const isMain = i <= 3
      out.push({
        index: i,
        label: isMain ? `主图${i}` : `详情图${i - 3}`,
        size: isMain ? UI_SIZE_MAIN : UI_SIZE_DETAIL,
        hint: isMain ? `核心界面${i}` : `扩展功能页${i - 3}`
      })
    }
  }
  return out
}

export function uiDesignScreenCountDropdownOptions() {
  return UI_DESIGN_SCREEN_COUNT_CHOICES.map(c => ({
    id: c,
    label: `${c} 张`
  }))
}

const CN_NUM = {
  一: 1,
  二: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
  十: 10
}

function parseChineseIndex(raw) {
  const s = String(raw || '').trim()
  if (!s) return 0
  if (/^\d+$/.test(s)) return parseInt(s, 10)
  if (s.length === 1 && CN_NUM[s] != null) return CN_NUM[s]
  if (s.startsWith('十')) {
    const rest = s.slice(1)
    return 10 + (CN_NUM[rest] || 0)
  }
  if (s.endsWith('十')) {
    const head = s.slice(0, -1)
    return (CN_NUM[head] || 0) * 10
  }
  const ten = s.indexOf('十')
  if (ten > 0) {
    const a = CN_NUM[s.slice(0, ten)] || 0
    const b = CN_NUM[s.slice(ten + 1)] || 0
    return a * 10 + b
  }
  return 0
}

function extractSizeFromSection(body) {
  const m = String(body || '').match(/\[\s*(\d+)\s*x\s*(\d+)\s*\]/i)
  if (!m) return ''
  return `${m[1]}x${m[2]}`
}

function stripSectionHeader(body) {
  return String(body || '')
    .replace(/^[^\n]*\n?/, '')
    .replace(/\[\s*\d+\s*x\s*\d+\s*\]/gi, '')
    .trim()
}

function defaultSizeForPlatform(platformId, index) {
  const p = String(platformId || 'app').trim()
  if (p === 'web') return UI_SIZE_DESKTOP
  return UI_SIZE_MOBILE
}

/**
 * 解析 DeepSeek 输出的多屏 UI 提示词
 * @returns {{ index: number, label: string, size: string, prompt: string }[]}
 */
const SECTION_HEAD_RE = /###\s*第([一二三四五六七八九十百\d]+)[屏张][^\n]*/

function pushUiDesignSection(out, raw, index, labelLine, chunk) {
  if (!index || index < 1) return
  const body = stripSectionHeader(String(chunk || '').replace(labelLine, ''))
  let size = extractSizeFromSection(chunk)
  if (!size) {
    size = index <= 3 ? UI_SIZE_MAIN : UI_SIZE_DETAIL
  }
  const prompt = body.trim()
  if (!prompt) return
  const seen = out.find(r => r.index === index)
  if (seen) {
    if (prompt.length > String(seen.prompt || '').length) seen.prompt = prompt
    return
  }
  out.push({
    index,
    label: labelLine.replace(/^###\s*/, '').trim() || `第${index}张`,
    size,
    prompt
  })
}

export function parseUiDesignSections(text) {
  const raw = String(text || '').trim()
  if (!raw) return []
  const out = []
  const chunks = raw.split(/(?=###\s*第)/).filter(c => /###\s*第/.test(c))
  for (const chunk of chunks) {
    if (/###\s*全局设计规范/.test(chunk) && !SECTION_HEAD_RE.test(chunk)) {
      continue
    }
    const head = chunk.match(SECTION_HEAD_RE)
    if (!head) continue
    const index = parseChineseIndex(head[1])
    pushUiDesignSection(out, raw, index, head[0], chunk.replace(head[0], ''))
  }
  if (!out.length) {
    const re = new RegExp(SECTION_HEAD_RE.source, 'g')
    let m
    while ((m = re.exec(raw)) !== null) {
      const index = parseChineseIndex(m[1])
      const start = m.index + m[0].length
      const rest = raw.slice(start)
      const next = rest.search(/\n###\s*第[一二三四五六七八九十百\d]+[屏张]/)
      const bodyChunk = next >= 0 ? rest.slice(0, next) : rest
      pushUiDesignSection(out, raw, index, m[0], bodyChunk)
    }
  }
  out.sort((a, b) => a.index - b.index)
  return out
}

export function alignUiDesignToPlan(parsed, ctx) {
  const byIndex = {}
  for (const row of parsed || []) {
    if (row && row.index > 0) byIndex[row.index] = row
  }
  const plat = String(ctx.platform || 'app').trim()
  const name = String(ctx.projectName || '').trim() || '产品'
  const primary = String(ctx.themeColorPrimary || UI_DESIGN_DEFAULT_PRIMARY).trim()
  const secondary = String(ctx.themeColorSecondary || UI_DESIGN_DEFAULT_SECONDARY).trim()
  const planTemplate = buildUiDesignPlanTemplate(ctx.screenCount)
  return planTemplate.map(plan => {
    const hit = byIndex[plan.index]
    const rawSize =
      (hit && hit.size) ||
      (plan.index <= 3 ? UI_SIZE_MAIN : UI_SIZE_DETAIL) ||
      defaultSizeForPlatform(plat, plan.index)
    const isMainSquare = plan.index <= 3 && !String(rawSize).includes('1536')
    const refStyle = String(
      ctx.referenceStyleGuide || ctx.upstreamImageInfo || ''
    ).trim()
    const prompt =
      hit && hit.prompt
        ? hit.prompt
        : [
            `UI 界面设计，${plan.label}，${plan.hint}。`,
            `待设计产品：${name}。`,
            String(ctx.brief || '').trim(),
            resolveUiDesignStyleText(ctx),
            refStyle ? `参考AI视觉风格：${refStyle.slice(0, 280)}` : '',
            `主色${primary}，辅色${secondary}。`,
            '页面功能须符合待设计产品，视觉对齐参考风格；微软雅黑、无边框纯界面、严格遵循全局设计规范声明。'
          ]
            .filter(Boolean)
            .join(' ')
    const row = {
      index: plan.index,
      label: (hit && hit.label) || `第${plan.index}张（${plan.label}）`,
      size: resolveEcomSeedreamOutputSize(rawSize, isMainSquare),
      prompt
    }
    return normalizeEcomPromptPlanRow(row)
  })
}

/**
 * 已拆分的子图片节点出图 URL（按 spawnedImageNodeIds 顺序，需传入 nodesById）
 * @param {object} node
 * @param {Record<string, object>} [nodesById]
 */
export function uiDesignOutputUrls(node, nodesById) {
  if (!node || node.type !== 'uiDesign' || !node.data) return []
  const ids = Array.isArray(node.data.spawnedImageNodeIds)
    ? node.data.spawnedImageNodeIds
    : []
  const map = nodesById && typeof nodesById === 'object' ? nodesById : {}
  const urls = []
  for (const id of ids) {
    const img = map[id]
    if (!img || img.type !== 'image' || !img.data) continue
    const u = String(img.data.imageUrl || '').trim()
    if (u) urls.push(u)
  }
  return urls
}

export function exportUiDesignNodeText(node) {
  if (!node || node.type !== 'uiDesign') return ''
  const d = node.data || {}
  const name = String(d.projectName || '').trim()
  const brief = String(d.brief || '').trim()
  const styleName = uiDesignStyleLabel(d)
  const plans = Array.isArray(d.screenPlans) ? d.screenPlans : []
  const parts = []
  if (name || brief || styleName) {
    parts.push(`【UI 设计】${[name, styleName, brief].filter(Boolean).join(' · ')}`)
  }
  if (plans.length) {
    parts.push(
      plans
        .map((p, i) => {
          const lab = p && p.label ? String(p.label) : `屏${i + 1}`
          const pr = p && p.prompt ? String(p.prompt).trim() : ''
          return pr ? `[${lab}] ${pr}` : ''
        })
        .filter(Boolean)
        .join('\n')
    )
  }
  return parts.join('\n').trim()
}
